import { useState } from "react";
import { useCustom, useCustomMutation } from "@refinedev/core";
import { Button, Modal, StatusBadge } from "@fawkes/ui";
import { KeyRound, Plus } from "lucide-react";

import { foutBody } from "./types";
import { IndeelbareTabel } from "../../components/IndeelbareTabel";

/**
 * Portaalinstellingen › API (ontwerp 16): de websites die via de externe API met het portaal werken. Per client vaste
 * redirect-URI's, scopes en een optionele IP-allowlist; een servertoepassing krijgt een secret dat alleen direct na
 * aanmaken of vernieuwen te zien is. Aanmaken kan pas na bevestiging van de verwerkersovereenkomst.
 */

type T = (key: string, options?: { args?: unknown[] }) => string;

interface Client {
  id: string;
  clientId: string;
  naam: string;
  soort: "PUBLIC" | "CONFIDENTIAL";
  redirectUris: string[];
  scopes: string[];
  ipAllowlist: string | null;
  verwerkersovereenkomstOp: string;
  aangemaaktDoor: string;
  aangemaaktOp: string;
  ingetrokkenOp: string | null;
  laatstGebruiktOp: string | null;
}

interface Scope { scope: string; uitleg: string }

interface Invoer {
  naam: string;
  soort: "PUBLIC" | "CONFIDENTIAL";
  redirectUris: string;
  scopes: string[];
  ipAllowlist: string;
  verwerkersovereenkomst: boolean;
}

const LEEG: Invoer = { naam: "", soort: "PUBLIC", redirectUris: "", scopes: ["portal.read"], ipAllowlist: "", verwerkersovereenkomst: false };

export function ApiClients({ t, tenantId, formatDatum }: { t: T; tenantId: string; formatDatum: (d: string | null) => string }) {
  const url = `portaal-instellingen/${tenantId}/api-clients`;
  const { data, refetch } = useCustom<Client[]>({ url, method: "get" });
  const { data: scopeData } = useCustom<Scope[]>({ url: `${url}/scopes`, method: "get" });
  const { mutateAsync, isLoading: bezig } = useCustomMutation();
  const [venster, setVenster] = useState<{ client: Client | null } | null>(null);
  const [secret, setSecret] = useState<{ clientId: string; secret: string } | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const clients = Array.isArray(data?.data) ? data.data : [];
  const scopes = Array.isArray(scopeData?.data) ? scopeData.data : [];
  const basis = typeof window === "undefined" ? "" : window.location.origin;

  async function doe(pad: string, method: "post" | "put" | "delete", values: object = {}) {
    setFout(null);
    try {
      const antwoord = await mutateAsync({ url: `${url}${pad}`, method, values, successNotification: false, errorNotification: false });
      await refetch();
      return antwoord.data as { client?: Client; secret?: string | null } | undefined;
    } catch (err) {
      const code = (err as { statusCode?: number })?.statusCode;
      setFout(code === 403 ? t("portaalInstellingen.api.geenRecht") : foutBody(err).melding ?? t("portaalInstellingen.fout"));
      return undefined;
    }
  }

  async function bewaar(invoer: Invoer, bestaand: Client | null) {
    const values = { ...invoer, redirectUris: invoer.redirectUris.split(/\s+/).filter(Boolean) };
    const antwoord = bestaand ? await doe(`/${bestaand.id}`, "put", values) : await doe("", "post", values);
    if (antwoord) {
      setVenster(null);
      if (antwoord.secret && antwoord.client) {
        setSecret({ clientId: antwoord.client.clientId, secret: antwoord.secret });
      }
    }
  }

  return (
    <section className="kvd__blok">
      <h2>{t("portaalInstellingen.tab.api")}</h2>
      <p className="kvd__stil">{t("portaalInstellingen.api.uitleg")}</p>
      <p className="kvd__stil">{t("portaalInstellingen.api.documentatie", { args: [`${basis}/v3/api-docs/extern`] })}</p>
      {fout && <p className="fawkes-error" role="alert">{fout}</p>}
      {secret && (
        <div className="pi__secret" role="status">
          <strong>{t("portaalInstellingen.api.secretEenmalig")}</strong>
          <code>{secret.clientId}</code>
          <code>{secret.secret}</code>
          <Button variant="secondary" onClick={() => setSecret(null)}>{t("portaalInstellingen.api.secretGezien")}</Button>
        </div>
      )}
      {clients.length === 0 ? <p className="kvd__stil">{t("portaalInstellingen.api.geen")}</p> : (
        <IndeelbareTabel
          tabelKey="portaal-api-clients"
          columns={[
            {
              key: "naam", header: t("portaalInstellingen.api.naam"),
              cell: (c) => (
                <>
                  {c.naam}{" "}
                  <StatusBadge tone={c.ingetrokkenOp ? "neutral" : "success"}>
                    {c.ingetrokkenOp
                      ? t("portaalInstellingen.api.ingetrokken")
                      : t(`portaalInstellingen.api.soort.${c.soort}`)}
                  </StatusBadge>
                  <br />
                  <span className="kvd__stil">{c.redirectUris.join(", ")}</span>
                </>
              ),
            },
            { key: "clientId", header: t("portaalInstellingen.api.clientId"), cell: (c) => <code>{c.clientId}</code> },
            { key: "scopes", header: t("portaalInstellingen.api.scopes"), cell: (c) => c.scopes.join(", ") },
            { key: "laatst", header: t("portaalInstellingen.api.laatst"), cell: (c) => formatDatum(c.laatstGebruiktOp) },
            {
              key: "acties", header: "", vast: true,
              cell: (c) => (!c.ingetrokkenOp ? (
                <span className="kpt__knoppen">
                  <button type="button" className="kpt__link" onClick={() => setVenster({ client: c })}>
                    {t("portaalInstellingen.api.wijzigen")}
                  </button>
                  {c.soort === "CONFIDENTIAL" && (
                    <button type="button" className="kpt__link" disabled={bezig}
                      onClick={async () => {
                        if (!window.confirm(t("portaalInstellingen.api.secretBevestig"))) return;
                        const a = await doe(`/${c.id}/secret`, "post");
                        if (a?.secret) setSecret({ clientId: c.clientId, secret: a.secret });
                      }}>
                      <KeyRound size={14} aria-hidden="true" /> {t("portaalInstellingen.api.nieuwSecret")}
                    </button>
                  )}
                  <button type="button" className="kpt__link kpt__link--gevaar" disabled={bezig}
                    onClick={() => {
                      if (window.confirm(t("portaalInstellingen.api.intrekkenBevestig", { args: [c.naam] }))) {
                        void doe(`/${c.id}`, "delete");
                      }
                    }}>
                    {t("portaalInstellingen.api.intrekken")}
                  </button>
                </span>
              ) : null),
            },
          ]}
          rows={clients}
          getRowKey={(c) => String(c.id)}
          empty=""
        />
      )}
      <div>
        <Button variant="secondary" onClick={() => { setFout(null); setVenster({ client: null }); }}>
          <Plus size={16} aria-hidden="true" /> {t("portaalInstellingen.api.nieuw")}
        </Button>
      </div>
      {venster && (
        <ClientVenster key={venster.client?.id ?? "nieuw"} t={t} client={venster.client} scopes={scopes} bezig={bezig} fout={fout}
          onClose={() => { setVenster(null); setFout(null); }} onBewaar={(i) => void bewaar(i, venster.client)} />
      )}
    </section>
  );
}

function ClientVenster({ t, client, scopes, bezig, fout, onClose, onBewaar }: {
  t: T;
  client: Client | null;
  scopes: Scope[];
  bezig: boolean;
  fout: string | null;
  onClose: () => void;
  onBewaar: (invoer: Invoer) => void;
}) {
  const [i, setI] = useState<Invoer>(client
    ? { naam: client.naam, soort: client.soort, redirectUris: client.redirectUris.join("\n"), scopes: client.scopes,
        ipAllowlist: client.ipAllowlist ?? "", verwerkersovereenkomst: true }
    : LEEG);
  const zet = (nieuw: Partial<Invoer>) => setI((oud) => ({ ...oud, ...nieuw }));
  return (
    <Modal open title={t(client ? "portaalInstellingen.api.wijzigen" : "portaalInstellingen.api.nieuw")} onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("klantverzoeken.annuleren")}</Button>
          <Button variant="primary" type="submit" form="pi-client" disabled={bezig || (!client && !i.verwerkersovereenkomst)}>
            {t("portaalInstellingen.opslaan")}
          </Button>
        </>
      }>
      <form id="pi-client" className="kvd__form" onSubmit={(e) => { e.preventDefault(); onBewaar(i); }}>
        <label>{t("portaalInstellingen.api.naam")}
          <input required maxLength={200} value={i.naam} onChange={(e) => zet({ naam: e.target.value })} />
        </label>
        {!client && (
          <label>{t("portaalInstellingen.api.soortLabel")}
            <select value={i.soort} onChange={(e) => zet({ soort: e.target.value as Invoer["soort"] })}>
              <option value="PUBLIC">{t("portaalInstellingen.api.soort.PUBLIC")}</option>
              <option value="CONFIDENTIAL">{t("portaalInstellingen.api.soort.CONFIDENTIAL")}</option>
            </select>
          </label>
        )}
        <label>{t("portaalInstellingen.api.redirects")}
          <textarea required rows={3} value={i.redirectUris} placeholder="https://www.pension.nl/inloggen/terug"
            onChange={(e) => zet({ redirectUris: e.target.value })} />
        </label>
        <fieldset className="kvd__dieren">
          <legend>{t("portaalInstellingen.api.scopes")}</legend>
          {scopes.map((s) => (
            <label key={s.scope} className="kvd__keuze">
              <input type="checkbox" checked={i.scopes.includes(s.scope)}
                onChange={(e) => zet({ scopes: e.target.checked ? [...i.scopes, s.scope] : i.scopes.filter((x) => x !== s.scope) })} />
              <code>{s.scope}</code> {s.uitleg}
            </label>
          ))}
        </fieldset>
        <label>{t("portaalInstellingen.api.ip")}
          <input value={i.ipAllowlist} placeholder="203.0.113.10, 198.51.100.0/24" onChange={(e) => zet({ ipAllowlist: e.target.value })} />
        </label>
        {!client && (
          <label className="kvd__keuze">
            <input type="checkbox" checked={i.verwerkersovereenkomst} onChange={(e) => zet({ verwerkersovereenkomst: e.target.checked })} />
            {t("portaalInstellingen.api.verwerkersovereenkomst")}
          </label>
        )}
        {fout && <p className="fawkes-error" role="alert">{fout}</p>}
      </form>
    </Modal>
  );
}
